import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Spin } from "antd";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import RouteCard from "./RouteCard";
import DocStatusPill from "./DocStatusPill";
import ShipModal from "./ShipModal";

// Supplier -- matches Diamond-PO-Prototype-v3.html's sRequests() exactly (lines 878-888): every
// doc with at least one REQUESTED line (header REQUESTED or PARTSHIP), card layout with an Open
// + Ship/Confirm-&-ship button per card. Ship now opens the real modal (Task 2180/F3).

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString()}`;
const toRouteAddress = (snap) =>
  snap && { Label: snap.label, ContactName: snap.contact, Address: snap.line1, City: snap.city, State: snap.state, Zip: snap.zip, Country: snap.country, Phone: snap.phone };

export const SupplierRequests = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shipDocId, setShipDocId] = useState(null);

  const load = () => {
    if (!dealerId) return;
    setLoading(true);
    Promise.all([
      PurchaseOrderService.GetDiamondDocList({ DealerID: String(dealerId), Status: "REQUESTED", Page: 1 }),
      PurchaseOrderService.GetDiamondDocList({ DealerID: String(dealerId), Status: "PARTSHIP", Page: 1 }),
    ])
      .then(([r1, r2]) => {
        setLoading(false);
        const list = [...(r1.data.status ? r1.data.responseData : []), ...(r2.data.status ? r2.data.responseData : [])];
        setDocs(list);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  return (
    <div style={{ padding: 20 }}>
      <Spin spinning={loading}>
        <h2 style={{ margin: 0 }}>{t("Requests")}</h2>
        <p style={{ color: "#888", margin: 0, marginBottom: 16 }}>{t("Memo requests and purchase orders waiting for you to ship")}</p>

        {docs.length === 0 ? (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>✓</div>
            {t("No requests waiting.")}
          </div>
        ) : (
          docs.map((d) => (
            <div key={d.DiamondDocID} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <h3 style={{ margin: 0, display: "inline" }}>{d.DocNumber}</h3> <DocStatusPill status={d.StatusCode} />{" "}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 7px",
                      borderRadius: 4,
                      background: d.DocKind === "PO" ? "#e9f7f4" : "#e8effc",
                      color: d.DocKind === "PO" ? "#0f766e" : "#2a52b8",
                    }}
                  >
                    {d.DocKind === "PO" ? t("DIRECT PURCHASE") : t("MEMO")}
                  </span>
                  <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
                    {d.RetailerName} · {fmtMoney((d.LineValue || 0) + (d.ChargeValue || 0))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button size="small" onClick={() => navigate(`/document/${d.DiamondDocID}`)}>
                    {t("Open")}
                  </Button>
                  <Button size="small" onClick={() => setShipDocId(d.DiamondDocID)}>
                    {d.DocKind === "PO" ? t("Confirm & ship") : t("Ship memo")}
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <RouteCard
                  fromAddress={toRouteAddress(typeof d.ShipFromJson === "string" ? JSON.parse(d.ShipFromJson) : d.ShipFromJson)}
                  toAddress={toRouteAddress(typeof d.ShipToJson === "string" ? JSON.parse(d.ShipToJson) : d.ShipToJson)}
                  fromPartyName={d.SupplierName}
                  toPartyName={d.RetailerName}
                />
              </div>
            </div>
          ))
        )}
      </Spin>
      {shipDocId && (
        <ShipModal
          docId={shipDocId}
          onClose={() => setShipDocId(null)}
          onDone={load}
        />
      )}
    </div>
  );
};

export default SupplierRequests;
